import {
  Assignment,
  CompressInPhaseRequest,
  UserSummary,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import { useActivityTemplateStore } from '@/store/activityTemplateStore';
import { useMilestoneProjectStore } from '@/store/milestoneProjectStore';
import { useMilestoneTemplateStore } from '@/store/milestoneTemplateStore';
import { usePhaseProjectRulesStore } from '@/store/phaseProjectRulesStore';
import { usePhaseProjectStore } from '@/store/phaseProjectStore';
import { usePhaseTemplateStore } from '@/store/phaseTemplateStore';
import { useProjectRulesStore } from '@/store/projectRulesStore';
import { useProjectStore } from '@/store/projectStore';
import { useMapStore } from '@/store/remapStore';
import { useTemplateStore } from '@/store/templateStore';
import { daysBetweenDates } from '@/utils/helpers/datetime';
import {
  calculateFinalMilestoneMaxDeadlinePosition,
  calculateFinalMilestoneMinDeadlinePosition,
} from '@/utils/helpers/milestone';
import { effectiveResponsibleUserOf } from '@/utils/helpers/user-helpers';
import {
  isConnectionViolatedByPosition,
  isRelationViolatedByPosition,
} from '@/utils/helpers/violatedEdges';
import { movePosition } from './movePosition';
import type { ItemState, ItemStatus, TaskItemState } from '@/common';

// map of id + index or position(ws)
interface IoStoreState {
  hiddenPhases: Map<string, number>;
  hiddenWorkstreams: Map<string, number>;
  // WI #5563: temporary "solo / focus" toggle — while set, only this workstream
  // is shown on the canvas map (it fills the available height) and every other
  // workstream falls into the "hidden workstreams" strip. Deliberately NOT
  // persisted to localStorage (cleared on reload / map switch); the persisted
  // `hiddenWorkstreams` set is left untouched so collapse restores it exactly.
  expandedWorkstreamId: string | null;
}

export interface MapPhase {
  id: string;
  title: string;
  start: number;
  duration: number;
  end: number;
  idx: number;
  deadline: string;
  responsible: string;
  responsibleUser?: Assignment['user'];
  isSpecialAssignment?: boolean;
  isHiddenOnMap: boolean;
  state: ItemState;
  status?: ItemStatus;
  completionUser?: UserSummary | null;
}

export interface MapWorkstream {
  id: string;
  title: string;
  position: number;
  description?: string;
  isHiddenOnMap: boolean;
}

export interface MapMilestone {
  id: string;
  idx: number;
  title: string;
  phase: string;
  isFinal: boolean;
  originalPosition: number;
  sequence: number;
  activities: MapActivity[];
  previousMsPosition: number;
  previousMsId: string;
  state: ItemState;
  status?: ItemStatus;
  deadline: string;
  externalDate?: string | null;
  description: string;
  mirrored?: boolean;
  sourceMilestoneId?: string;
  sourceProjectId?: string;
  sourceProjectTitle?: string;
  isHiddenOnMap: boolean;
}

export interface MapTask {
  id: string;
  title: string;
  responsible: string;
  responsibleUser?: Assignment['user'];
  isSpecialAssignment?: boolean;
  state: TaskItemState;
  status?: ItemStatus;
  deadline: string;
  completionUser?: UserSummary | null;
}

export interface MapActivity {
  id: string;
  title: string;
  predecessors: string[];
  successors: string[];
  workstream: string;
  responsible: string;
  responsibleUser?: Assignment['user'] | string;
  isSpecialAssignment?: boolean;
  criteria: MapTask[];
  actions: MapTask[];
  actionsResponsibles: string[];
  actionsResponsibleUsers: string[];
  criteriaResponsibles: string[];
  criteriaResponsibleUsers: string[];
  criteriaDeadlines: string[];
  phase: string;
  parent?: string | null;
  originalPosition: number; // activity.position + phase.start
  sequence: number;
  milestone?: string;
  required: boolean;
  tags: string[];
  status: ItemStatus;
  state: ItemState;
  deadline: string;
  description: string;
  mirrored?: boolean;
  sourceActivityId?: string;
  sourceProjectId?: string;
  sourceProjectTitle?: string;
  isHiddenOnMap: boolean;
  completionUser?: UserSummary | null;
  isFavorite?: boolean;
  approverFunctions: string[];
  approverUsers: UserSummary[];
}

export interface MapFunction {
  id: string;
  name: string;
  color: string;
  label: '#333' | '#eee';
  user: UserSummary | null;
}

export const defaultColors = {
  color: '#000',
  label: '#fff',
};

export const useIoStore = defineStore('ioStore', {
  state: (): IoStoreState => ({
    hiddenPhases: new Map([]),
    hiddenWorkstreams: new Map([]),
    expandedWorkstreamId: null,
  }),

  getters: {
    mapId(): string {
      return useMapStore().mapId;
    },
    isTemplate(): boolean {
      return useMapStore().isTemplate;
    },

    isExportingMapPdf(): boolean {
      return useMapStore().isExportingPDF;
    },

    projectStart(): string {
      if (this.isTemplate) {
        return '0';
      }

      return useProjectStore().project(this.mapId).start_date;
    },

    projectDuration(): number {
      if (!this.phases.length) {
        return 0;
      }

      if (this.isTemplate) {
        return this.phases[this.phases.length - 1].end;
      }

      const startDateStr = this.projectStart;
      const endDateStr = this.phases[this.phases.length - 1].deadline;

      return daysBetweenDates(startDateStr, endDateStr);
    },

    mapLength(): number {
      if (!this.phases.length) {
        return 0;
      }

      const lastPhase = this.phases[this.phases.length - 1] as MapPhase;

      return lastPhase ? lastPhase.end : 2000;
    },

    virtualMapLength(): number {
      if (this.hiddenPhases.size > 0) {
        return this.lastVisiblePhase.end;
      }

      return this.mapLength;
    },

    workstreams(): MapWorkstream[] {
      if (!this.mapId) {
        return [];
      }
      const workstreams = this.isTemplate
        ? useTemplateStore().template(this.mapId).workstreams
        : useProjectStore().project(this.mapId).workstreams;

      // WI #5563: while a workstream is expanded (solo), it is the only visible
      // one — every other workstream is treated as hidden WITHOUT touching the
      // persisted `hiddenWorkstreams` set. The expand is ignored if its target
      // no longer exists (deleted), which auto-collapses back to the persisted
      // visible/hidden state.
      const expandedId =
        this.expandedWorkstreamId &&
        workstreams.some((stream) => stream.id === this.expandedWorkstreamId)
          ? this.expandedWorkstreamId
          : null;

      return workstreams.map((stream, i) => ({
        id: stream.id,
        title: stream.title,
        position: i,
        description: stream.description,
        isHiddenOnMap: expandedId
          ? stream.id !== expandedId
          : this.hiddenWorkstreams.has(stream.id),
      }));
    },

    /**
     * WI #5563: ids of workstreams effectively hidden from the map — the
     * persisted hidden set, or (while a workstream is expanded) every workstream
     * except the expanded one. Single source of truth so activity visibility and
     * the placeholder strip both honour the temporary expand without mutating the
     * persisted `hiddenWorkstreams` set.
     */
    hiddenWorkstreamIdsOnMap(): Set<string> {
      return new Set(
        this.workstreams.filter((ws) => ws.isHiddenOnMap).map((ws) => ws.id),
      );
    },

    /** WI #5563: count of effectively-hidden workstreams (gates the strip). */
    hiddenWorkstreamsOnMapCount(): number {
      return this.hiddenWorkstreamIdsOnMap.size;
    },

    firstVisibleWorkstream(): MapWorkstream {
      const visibleStream = this.workstreams.find(
        (stream) => !stream.isHiddenOnMap,
      );

      if (!visibleStream) {
        return this.workstreams[0];
      }

      return visibleStream;
    },

    lastVisibleWorkstream(): MapWorkstream {
      const visibleStream = this.workstreams
        .toReversed()
        .find((stream) => !stream.isHiddenOnMap);

      if (!visibleStream) {
        return this.workstreams[this.workstreams.length - 1];
      }

      return visibleStream;
    },

    phases(): MapPhase[] {
      if (!this.mapId) {
        return [];
      }
      const phases: MapPhase[] = [];

      if (this.isTemplate) {
        const rawPhases = useTemplateStore().template(this.mapId).phases;
        let nextStart = 0;
        rawPhases.forEach((phase, i) => {
          const mapPhase: MapPhase = {
            id: phase.id,
            title: phase.title,
            start: nextStart,
            responsible: phase.responsible || '',
            duration: phase.duration,
            end: nextStart + phase.duration,
            idx: i,
            deadline: '',
            state: 'unplanned' as ItemState,
            isHiddenOnMap: this.hiddenPhases.has(phase.id),
          };
          phases.push(mapPhase);
          nextStart = mapPhase.end;
        });

        return phases.sort((a, b) => (a.start > b.start ? 1 : -1));
      }

      const project = useProjectStore().project(this.mapId);
      const rawPhases = project.phases;
      const startDate = project.start_date;
      let nextStart = 0;
      rawPhases.forEach((phase, i) => {
        const deadline = daysBetweenDates(startDate, phase.deadline);
        const mapPhase: MapPhase = {
          id: phase.id,
          title: phase.title,
          start: nextStart,
          duration: deadline - nextStart,
          end: deadline,
          idx: i,
          deadline: phase.deadline,
          responsible: phase.responsible.function_id || '',
          responsibleUser: getResponsibleUser(this.mapId, phase.responsible),
          isSpecialAssignment: phase.responsible.special_assignment,
          state: phase.state,
          status: phase.final_milestone.status || undefined,
          isHiddenOnMap: this.hiddenPhases.has(phase.id),
          completionUser: phase.completion_user,
        };
        phases.push(mapPhase);
        nextStart = deadline;
      });

      return phases.sort((a, b) => (a.start > b.start ? 1 : -1));
    },

    firstVisiblePhase(): MapPhase {
      const visiblePhase = this.phases.find((phase) => !phase.isHiddenOnMap);

      if (!visiblePhase) {
        return this.phases[0];
      }

      return visiblePhase;
    },

    lastVisiblePhase(): MapPhase {
      const visiblePhase = this.phases
        .toReversed()
        .find((phase) => !phase.isHiddenOnMap);

      if (!visiblePhase) {
        return this.phases[this.phases.length - 1];
      }

      return visiblePhase;
    },

    milestones(): MapMilestone[] {
      if (!this.mapId) {
        return [];
      }
      if (this.isTemplate) {
        const milestones = useTemplateStore().milestones(this.mapId);
        let prePos = 0;
        let preId = '';
        const mss = milestones
          .map((ms, idx): MapMilestone => {
            const originalPosition =
              ms.position + this.phase(ms.metadata.phaseId).start;

            return {
              id: ms.id,
              idx,
              title: ms.title,
              phase: ms.metadata.phaseId,
              isFinal: ms.metadata.isFinal,
              originalPosition,
              sequence: ms.sequence,
              activities: this.activities.filter(
                ({ milestone }) => milestone === ms.id,
              ),
              previousMsPosition: 0,
              previousMsId: '',
              state: 'planned' as ItemState,
              deadline: '',
              description: ms.description || '',
              isHiddenOnMap: this.hiddenPhases.has(ms.metadata.phaseId),
            };
          })
          .sort((a, b) => (a.originalPosition > b.originalPosition ? 1 : -1));

        mss.forEach((ms) => {
          ms.previousMsPosition = prePos;
          ms.previousMsId = preId;
          prePos = ms.originalPosition;
          preId = ms.id;
        });

        return mss;
      }

      // Project
      const startDate = useProjectStore().project(this.mapId).start_date;
      const milestones = useProjectStore().milestones(this.mapId).slice();
      const mirroredMilestones = useProjectStore().project(
        this.mapId,
      ).mirrored_milestones;
      milestones.sort((a, b) =>
        a.current_deadline > b.current_deadline ? 1 : -1,
      );
      mirroredMilestones.sort((a, b) =>
        a.current_deadline > b.current_deadline ? 1 : -1,
      );
      let prePos = 0;
      let preId = '';
      const mappedMilestones = milestones
        .map((ms, idx): MapMilestone => {
          const previousMsPosition = prePos;
          const previousMsId = preId;
          const originalPosition = daysBetweenDates(
            startDate,
            ms.current_deadline,
          );
          prePos = originalPosition;
          preId = ms.id;

          return {
            id: ms.id,
            idx,
            title: ms.title,
            phase: ms._phaseId,
            isFinal: ms.isFinal,
            originalPosition,
            sequence: ms.number,
            activities: this.activities.filter(
              ({ milestone }) => milestone === ms.id,
            ),
            previousMsPosition,
            previousMsId,
            state: ms.state,
            status: ms.status || undefined,
            deadline: ms.current_deadline,
            externalDate: ms.external_date ?? null,
            description: ms.description || '',
            mirrored: false,
            isHiddenOnMap: this.hiddenPhases.has(ms._phaseId),
          };
        })
        .sort((a, b) => (a.originalPosition > b.originalPosition ? 1 : -1));
      if (!mirroredMilestones) {
        return mappedMilestones;
      }
      preId = '';
      prePos = 0;
      const mappedMirroredMilestones = mirroredMilestones.map((milestone) => {
        const previousMsId = preId;
        const previousMsPosition = prePos;
        const originalPosition = daysBetweenDates(
          startDate,
          milestone.current_deadline,
        );
        prePos = originalPosition;
        preId = milestone.id;

        const positionedPhase = this.phaseByPosition(originalPosition); // the phase where the mirrored milestone is positioned

        return {
          id: milestone.id,
          idx: 0,
          title: milestone.title,
          phase: '',
          isFinal: false,
          sequence: milestone.number,
          activities: [],
          previousMsPosition: previousMsPosition,
          previousMsId: previousMsId,
          state: milestone.state || '',
          status: milestone.status as ItemStatus,
          originalPosition,
          mirrored: true,
          deadline: milestone.current_deadline,
          description: milestone.description,
          sourceMilestoneId: milestone.source_milestone_id,
          sourceProjectId: milestone.source_project_id,
          sourceProjectTitle: milestone.project_title,
          isHiddenOnMap: this.hiddenPhases.has(positionedPhase.id),
        };
      });

      return [...mappedMilestones, ...mappedMirroredMilestones];
    },

    activities(): MapActivity[] {
      if (!this.mapId) {
        return [];
      }
      if (this.isTemplate) {
        const activities = useTemplateStore().template(this.mapId).activities;

        return activities.map((activity) => {
          return {
            id: activity.id,
            title: activity.title,
            workstream: activity.workstream,
            phase: activity.phase,
            responsible: activity.responsible || '',
            originalPosition:
              activity.position + this.phase(activity.phase).start,
            sequence: activity.sequence,
            predecessors: activity.predecessors,
            successors: useTemplateStore()
              .successorsList(this.mapId, activity.id)
              .map(({ id }) => id),
            criteria: activity.completion_criteria.map((crit) => ({
              id: crit.id,
              title: crit.title,
              responsible: crit.responsible || '',
              deadline: '',
              state: 'planned',
            })),
            actions: activity.completion_criteria.map((action) => ({
              id: action.id,
              title: action.title,
              responsible: action.responsible || '',
              deadline: '',
              state: 'planned',
            })),
            actionsResponsibleUsers: [],
            actionsResponsibles: [],
            criteriaResponsibles: activity.completion_criteria
              .map((crit) => crit.responsible)
              .filter((responsible) => responsible !== null) as string[],
            criteriaResponsibleUsers: [],
            criteriaDeadlines: [],
            parent: activity.parent,
            milestone: activity.milestone?.id
              ? activity.milestone.id
              : undefined,
            required: !!activity.milestone?.required,
            tags: activity.tags,
            state: 'unplanned' as ItemState,
            status: 'red' as ItemStatus,
            deadline: '',
            description: activity.description || '',
            isHiddenOnMap:
              this.hiddenPhases.has(activity.phase) ||
              this.hiddenWorkstreamIdsOnMap.has(activity.workstream),
            approverFunctions: activity.approvers ?? [],
            approverUsers: [],
          };
        });
      }
      const { activities, start_date, mirrored_activities } =
        useProjectStore().project(this.mapId);
      const mappedActivities = activities.map((activity) => {
        const { approvers = [] } = activity;
        const successorsList = useProjectStore()
          .successorsList(this.mapId, activity.id)
          .map(({ id }) => id);
        const mirroredPredecessors = mirrored_activities
          ? mirrored_activities
              .filter((m) => m.successors.includes(activity.id))
              .map((m) => m.id)
          : [];
        const mirroredSuccessors = mirrored_activities
          ? mirrored_activities
              .filter((m) => m.predecessors.includes(activity.id))
              .map((m) => m.id)
          : [];

        return {
          id: activity.id,
          title: activity.title,
          workstream: activity.workstream,
          phase: activity.phase,
          responsible: activity.responsible.function_id || '',
          responsibleUser: getResponsibleUser(this.mapId, activity.responsible),
          isSpecialAssignment: activity.responsible.special_assignment,
          originalPosition: daysBetweenDates(
            start_date,
            activity.current_deadline,
          ),
          sequence: activity.number,
          predecessors: [...activity.predecessors, ...mirroredPredecessors],
          successors: [...successorsList, ...mirroredSuccessors],
          actionsResponsibles: activity.actions
            .map((action) => action.responsible.function_id)
            .filter((responsible) => responsible !== null) as string[],
          // Deliberately the *displayed* responsible, not the raw one: these
          // usernames drive the map's user filter, which must stay in step with
          // the names on screen.
          actionsResponsibleUsers: activity.actions
            .map(
              (action) =>
                effectiveResponsibleUserOf(action, action.responsible.user)
                  ?.username,
            )
            .filter((user): user is string => !!user),
          criteriaResponsibles: activity.completion_criteria
            .map((crit) => crit.responsible.function_id)
            .filter((responsible) => responsible !== null) as string[],
          criteriaResponsibleUsers: activity.completion_criteria
            .map(
              (crit) =>
                effectiveResponsibleUserOf(crit, crit.responsible.user)
                  ?.username,
            )
            .filter((user): user is string => !!user),
          criteria: activity.completion_criteria.map((crit) => ({
            id: crit.id,
            title: crit.title,
            responsible: crit.responsible.function_id || '',
            responsibleUser: crit.responsible.user,
            isSpecialAssignment: crit.responsible.special_assignment,
            deadline: crit.current_deadline,
            state: crit.state,
            status: crit.status as ItemStatus,
            completionUser: crit.completion_user,
          })),
          actions: activity.actions.map((action) => ({
            id: action.id,
            title: action.title,
            responsible: action.responsible.function_id || '',
            responsibleUser: action.responsible.user,
            isSpecialAssignment: action.responsible.special_assignment,
            deadline: action.current_deadline,
            state: action.state,
            status: action.status as ItemStatus,
            completionUser: action.completion_user,
          })),
          criteriaDeadlines: activity.completion_criteria.map(
            (crit) => crit.current_deadline,
          ),
          milestone: activity.milestone?.milestone
            ? activity.milestone.milestone
            : undefined,
          required: !!activity.milestone?.required,
          tags: activity.tags,
          status: activity.status as ItemStatus,
          state: activity.state || '',
          deadline: activity.current_deadline,
          description: activity.description,
          mirrored: false,
          isHiddenOnMap:
            this.hiddenPhases.has(activity.phase) ||
            this.hiddenWorkstreamIdsOnMap.has(activity.workstream),
          completionUser: activity.completion_user,
          isFavorite: activity.is_favorite,
          approverFunctions: approvers,
          approverUsers: functionHolders(this.mapId, approvers),
        };
      });
      if (!mirrored_activities) {
        return mappedActivities;
      }
      const mappedMirroredActivities = mirrored_activities.map((activity) => {
        const originalPosition = daysBetweenDates(
          start_date,
          activity.current_deadline,
        );
        const positionedPhase = this.phaseByPosition(originalPosition); // the phase where the mirrored activity is positioned

        return {
          id: activity.id,
          title: activity.title,
          workstream: activity.workstream_in_project,
          phase: '',
          responsible: activity.responsible_function,
          responsibleUser: activity.responsible_user,
          isSpecialAssignment: false,
          originalPosition,
          sequence: activity.number,
          predecessors: activity.predecessors,
          successors: activity.successors,
          actionsResponsibles: [],
          actionsResponsibleUsers: [],
          criteriaResponsibles: [],
          criteriaResponsibleUsers: [],
          criteria: [],
          actions: [],
          criteriaDeadlines: [],
          milestone: undefined,
          required: false,
          tags: [],
          status: activity.status as ItemStatus,
          state: activity.state || '',
          deadline: activity.current_deadline,
          description: activity.description,
          mirrored: true,
          sourceActivityId: activity.source_activity_id,
          sourceProjectId: activity.source_project_id,
          sourceProjectTitle: activity.project_title,
          isHiddenOnMap:
            this.hiddenWorkstreamIdsOnMap.has(activity.workstream_in_project) ||
            this.hiddenPhases.has(positionedPhase.id),
          approverFunctions: [],
          approverUsers: [],
        };
      });

      return [...mappedActivities, ...mappedMirroredActivities];
    },

    activitiesInPhase() {
      return (phaseId: string) => {
        return this.activities.filter(({ phase }) => phase === phaseId);
      };
    },

    mirroredActivitiesInPhase() {
      return (phaseId: string) => {
        return this.activities
          .filter((activity) => activity.mirrored)
          .filter((activity) => {
            try {
              const phaseForActivity = this.phaseByDate(activity.deadline);

              return phaseForActivity.id === phaseId;
            } catch {
              return false;
            }
          });
      };
    },

    mirroredMilestonesInPhase() {
      return (phaseId: string) => {
        return this.milestones
          .filter((milestone) => milestone.mirrored)
          .filter((milestone) => {
            try {
              const phaseForMilestone = this.phaseByDate(milestone.deadline);

              return phaseForMilestone.id === phaseId;
            } catch {
              return false;
            }
          });
      };
    },

    intermediateMilestonesInPhase() {
      return (phaseId: string): MapMilestone[] => {
        const store = this.isTemplate ? useTemplateStore() : useProjectStore();
        const phase = store.phase(this.mapId, phaseId);

        return phase.intermediate_milestones.map(({ id }) =>
          this.milestone(id),
        );
      };
    },

    finalMilestoneOfPhase() {
      return (phaseId: string): MapMilestone => {
        const store = this.isTemplate ? useTemplateStore() : useProjectStore();
        const phase = store.phase(this.mapId, phaseId);

        return this.milestone(phase.final_milestone.id);
      };
    },

    functions(): MapFunction[] {
      if (!this.mapId) {
        return [];
      }
      if (this.isTemplate) {
        const fns = [...useTemplateStore().template(this.mapId).functions].sort(
          (a, b) => a.name.localeCompare(b.name),
        );

        return fns.map((fn) => ({
          id: fn.id,
          name: fn.name,
          color: fn.color,
          label: fn.label === 'light' ? '#eee' : '#333',
          user: null,
        }));
      }
      const fns = [...useProjectStore().project(this.mapId).functions].sort(
        (a, b) => a.name.localeCompare(b.name),
      );

      return fns.map((fn) => ({
        id: fn.id,
        name: fn.name,
        color: fn.color,
        label: fn.label === 'light' ? '#eee' : '#333',
        user: fn.user,
      }));
    },

    phase() {
      return (phaseId: string): MapPhase => {
        const phase = this.phases.find(({ id }) => id === phaseId);
        if (!phase) {
          throw new Error('Phase not found ' + phaseId);
        }

        return phase;
      };
    },

    phaseByPosition() {
      return (x: number): MapPhase => {
        const phases = this.phases.filter(({ start }) => start < x);
        phases.sort((a, b) => {
          if (a.start === b.start) {
            return 0;
          }

          return a.start > b.start ? -1 : 1;
        });
        const phase = phases[0];
        if (!phase) {
          throw new Error('Phase not found in position' + x);
        }

        return phase;
      };
    },

    phaseByIdx() {
      return (idx: number): MapPhase => {
        const phase = this.phases.find(({ idx: i }) => i === idx);
        if (!phase) {
          throw new Error('Phase not found with idx' + idx);
        }

        return phase;
      };
    },

    phaseByDate() {
      return (date: string): MapPhase => {
        const phase = this.phases
          .filter(({ deadline }) => date <= deadline)
          .sort((a, b) => (a.start > b.start ? 1 : -1))
          .at(0);
        if (!phase) {
          throw new Error('Phase not found for date ' + date);
        }

        return phase;
      };
    },

    workstreamById() {
      return (streamId: string): MapWorkstream => {
        const stream = this.workstreams.find(({ id }) => id === streamId);
        if (!stream) {
          throw new Error('Workstream not found ' + streamId);
        }

        return stream;
      };
    },

    workstreamByPosition() {
      return (position: number): MapWorkstream => {
        const stream = this.workstreams.find((ws) => ws.position === position);
        if (!stream) {
          throw new Error('Workstream not found ' + position);
        }

        return stream;
      };
    },

    activity() {
      return (activityId: string): MapActivity => {
        const activity = this.activities.find(({ id }) => id === activityId);
        if (!activity) {
          throw new Error('Activity not found ' + activityId);
        }

        return activity;
      };
    },

    milestone() {
      return (msId: string): MapMilestone => {
        const milestone = this.milestones.find(({ id }) => id === msId);
        if (!milestone) {
          throw new Error('Milestone not found ' + msId);
        }

        return milestone;
      };
    },

    function() {
      return (fnId: string): MapFunction => {
        const fun = this.functions.find(({ id }) => id === fnId);
        if (!fun) {
          throw new Error('Function not found ' + fnId);
        }

        return fun;
      };
    },

    activitiesInWorkstream() {
      const wsGroups = this.workstreams.reduce(
        (acc, cur) => {
          acc[cur.id] = this.activities
            .filter(({ workstream }) => workstream === cur.id)
            .sort((a, b) => {
              if (a.originalPosition === b.originalPosition) {
                return a.sequence > b.sequence ? 1 : -1;
              }

              return a.originalPosition > b.originalPosition ? 1 : -1;
            });

          return acc;
        },
        {} as Record<string, MapActivity[]>,
      );

      return (wsId: string) => {
        const activities = wsGroups[wsId];
        if (!activities) {
          throw new Error('missing workstream');
        }

        return activities;
      };
    },

    activityIndexInWs() {
      return (activityId: string, workstreamId: string) => {
        return this.activitiesInWorkstream(workstreamId).findIndex(
          ({ id }) => id === activityId,
        );
      };
    },

    successorsList() {
      return (activityId: string) => {
        return this.activities.filter(({ predecessors }) =>
          predecessors.includes(activityId),
        );
      };
    },

    // Only edges that are currently valid clamp a move. A kept
    // violated relation (predecessor already after its successor) or milestone
    // connection (activity outside the milestone's phase or after it) does not
    // bound the drag and heals by itself once the order is right again.
    // Template maps keep every edge (their force path still unlinks).
    isRelationValid() {
      return (predecessor: MapActivity, successor: MapActivity): boolean =>
        this.isTemplate ||
        !isRelationViolatedByPosition(
          predecessor.originalPosition,
          successor.originalPosition,
        );
    },

    // the connected activities of a milestone whose connection is currently valid
    validMilestoneActivities() {
      return (msId: string): MapActivity[] => {
        const milestone = this.milestone(msId);
        if (this.isTemplate) {
          return milestone.activities;
        }

        return milestone.activities.filter(
          (activity) => !isConnectionViolatedByPosition(activity, milestone),
        );
      };
    },

    // the milestone an activity is connected to, only while that connection is valid
    validActivityMilestone() {
      return (activityId: string): MapMilestone | undefined => {
        const activity = this.activity(activityId);
        const milestone = activity?.milestone
          ? this.milestone(activity.milestone)
          : undefined;
        if (!milestone) {
          return undefined;
        }

        return this.isTemplate ||
          !isConnectionViolatedByPosition(activity, milestone)
          ? milestone
          : undefined;
      };
    },

    lastActivityPredecessor() {
      return (activityId: string): MapActivity | undefined => {
        const activity = this.activity(activityId);
        const lastPredecessor = activity.predecessors
          .map((id) => this.activity(id))
          .filter((predecessor) => this.isRelationValid(predecessor, activity))
          .sort((a, b) => {
            if (a.originalPosition === b.originalPosition) {
              return 0;
            }

            return a.originalPosition > b.originalPosition ? 1 : -1;
          })
          .pop();

        return lastPredecessor;
      };
    },

    lastActivityPredecessorThatIsNotInAListOfActivities() {
      return (
        activityId: string,
        activityIds: string[],
      ): MapActivity | undefined => {
        const activity = this.activity(activityId);
        const lastPredecessor = activity.predecessors
          .filter((p) => !activityIds.includes(p))
          .map((id) => this.activity(id))
          .filter((predecessor) => this.isRelationValid(predecessor, activity))
          .sort((a, b) => {
            if (a.originalPosition === b.originalPosition) {
              return 0;
            }

            return a.originalPosition > b.originalPosition ? 1 : -1;
          })
          .pop();

        return lastPredecessor;
      };
    },

    firstActivitySuccessor() {
      return (activityId: string): MapActivity | undefined => {
        const activity = this.activity(activityId);
        const firstSuccessor = this.successorsList(activityId)
          .filter((successor) => this.isRelationValid(activity, successor))
          .sort((a, b) => {
            if (a.originalPosition === b.originalPosition) {
              return 0;
            }

            return a.originalPosition < b.originalPosition ? 1 : -1;
          })
          .pop();

        return firstSuccessor;
      };
    },

    firstActivitySuccessorThatIsNotInAListOfActivities() {
      return (
        activityId: string,
        activityIds: string[],
      ): MapActivity | undefined => {
        const activity = this.activity(activityId);
        const firstSuccessor = this.successorsList(activityId)
          .filter((p) => !activityIds.includes(p.id))
          .filter((successor) => this.isRelationValid(activity, successor))
          .sort((a, b) => {
            if (a.originalPosition === b.originalPosition) {
              return 0;
            }

            return a.originalPosition < b.originalPosition ? 1 : -1;
          })
          .pop();

        return firstSuccessor;
      };
    },

    functionColors() {
      return (fnId: string | null) => {
        if (!fnId) {
          return defaultColors;
        }
        const fn = this.functions.find(({ id }) => id === fnId);
        if (!fn) {
          return defaultColors;
        }

        return fn;
      };
    },

    activityColors() {
      return (activityId: string) => {
        const activity = this.activity(activityId);

        return this.functionColors(activity.responsible);
      };
    },
  },

  actions: {
    async addWorkstream(title: string, before: string | undefined) {
      const store = this.isTemplate ? useTemplateStore() : useProjectStore();

      return store.addWorkstream(this.mapId, { title }, before);
    },

    async updateWorkstream(
      title: string,
      before: string,
      description?: string,
    ) {
      const store = this.isTemplate ? useTemplateStore() : useProjectStore();
      const ws = await store.updateWorkstream(
        this.mapId,
        useMapStore().selectedId,
        {
          title,
          description,
        },
      );
      const ids = this.workstreams.map(({ id }) => id);
      const newOrder = movePosition(ids, useMapStore().selectedId, before);
      await store.update(this.mapId, {
        workstreams_order: newOrder,
      });

      return ws;
    },

    async deleteWorkstream(streamId: string) {
      const store = this.isTemplate ? useTemplateStore() : useProjectStore();
      await store.deleteWorkstream(this.mapId, streamId);
    },

    async deletePhase(phaseId: string) {
      const store = this.isTemplate
        ? usePhaseTemplateStore()
        : usePhaseProjectStore();

      return store.remove(this.mapId, phaseId);
    },

    async deleteMilestone(msId: string) {
      const store = this.isTemplate
        ? useMilestoneTemplateStore()
        : useMilestoneProjectStore();

      return store.remove(this.mapId, msId);
    },

    deleteActivity(activityId: string) {
      const store = this.isTemplate
        ? useActivityTemplateStore()
        : useActivityProjectStore();

      return store.remove(this.mapId, activityId);
    },

    async deleteActivities(activityIds: string[]) {
      const store = this.isTemplate
        ? useActivityTemplateStore()
        : useActivityProjectStore();

      return await store.removeActivities(this.mapId, activityIds);
    },

    async duplicateActivity(activityId: string) {
      const store = this.isTemplate
        ? useActivityTemplateStore()
        : useActivityProjectStore();
      const payload = {
        duplicate_ids: [activityId],
      };
      await store.duplicate(this.mapId, payload);
    },

    async duplicateActivities(activityIds: string[]) {
      const store = this.isTemplate
        ? useActivityTemplateStore()
        : useActivityProjectStore();
      const payload = {
        duplicate_ids: activityIds,
      };
      await store.duplicate(this.mapId, payload);
    },

    async applySpacePhaseTemplate(
      phase: MapPhase,
      activities: MapActivity[],
      milestones: MapMilestone[],
      offset: number,
    ) {
      const templateStore = useTemplateStore();
      const payload = {
        activity_positions: activities.map((activity) => ({
          id: activity.id,
          position: activity.originalPosition - offset - phase.start,
        })),
        milestone_positions: milestones
          .filter((ms) => !ms.isFinal)
          .map((ms) => ({
            id: ms.id,
            position: ms.originalPosition - offset - phase.start,
          })),
      };

      return await usePhaseTemplateStore()
        .update(this.mapId, phase.id, payload)
        .then(() => templateStore.loadTemplate(this.mapId));
    },

    async applySpaceFullTemplate(
      mapPhase: MapPhase,
      activities: MapActivity[],
      milestones: MapMilestone[],
      offset: number,
    ): Promise<unknown> {
      const templateStore = useTemplateStore();
      const phase = templateStore.phase(this.mapId, mapPhase.id);
      const payload = {
        activity_positions: activities.map((activity) => ({
          id: activity.id,
          position: activity.originalPosition - offset - mapPhase.start,
        })),
        duration: phase.duration - offset,
        milestone_positions: milestones.map((ms) => ({
          id: ms.id,
          position: ms.originalPosition - offset - mapPhase.start,
        })),
      };

      return await useTemplateStore()
        .updatePhase(this.mapId, mapPhase.id, payload)
        .then(() => templateStore.loadTemplate(this.mapId));
    },

    async applyCompressPhaseProject(
      mapPhase: MapPhase,
      startDay: number,
      offset: number,
      direction: 'right' | 'left',
      fixedMilestoneIds: string[] = [],
    ): Promise<unknown> {
      const projectStore = useProjectStore();
      const phaseRulesStore = usePhaseProjectRulesStore();
      const phaseStore = usePhaseProjectStore();

      const deadline = new Date(projectStore.project(this.mapId).start_date);
      deadline.setUTCDate(deadline.getUTCDate() + startDay);

      const payload: CompressInPhaseRequest = {
        start_day: deadline.toISOString(),
        compress_days: offset,
        direction: direction,
      };
      // WI #5622 — only sent when something is fixed, so a plain compress is
      // the same request as before.
      if (fixedMilestoneIds.length) {
        payload.fixed_milestone_ids = fixedMilestoneIds;
      }

      const errors = phaseRulesStore.validateApplyCompressToolRequest(
        this.mapId,
        mapPhase.id,
        deadline,
        direction,
        fixedMilestoneIds,
        offset,
      );

      if (errors) {
        throw errors;
      }

      return phaseStore
        .applyCompressTool(this.mapId, mapPhase.id, payload)
        .then(() => projectStore.loadProject(this.mapId));
    },

    async applySpacePhaseProject(
      mapPhase: MapPhase,
      activities: MapActivity[],
      milestones: MapMilestone[],
      offset: number,
    ): Promise<unknown> {
      const projectStore = useProjectStore();
      const phaseRulesStore = usePhaseProjectRulesStore();
      const phaseStore = usePhaseProjectStore();

      const payload = {
        activity_deadlines: activities.map(({ id }) => {
          const activity = useProjectStore().activitySummary(this.mapId, id);
          const deadline = new Date(activity.current_deadline);
          deadline.setUTCDate(deadline.getUTCDate() - offset);

          return {
            id: id,
            deadline: deadline.toISOString(),
          };
        }),
        milestone_deadlines: milestones
          .filter((ms) => !ms.isFinal)
          .map(({ id }) => {
            const ms = useProjectStore().milestone(this.mapId, id);
            const deadline = new Date(ms.current_deadline);
            deadline.setUTCDate(deadline.getUTCDate() - offset);

            return {
              id: id,
              deadline: deadline.toISOString(),
            };
          }),
      };

      const errors = phaseRulesStore.validateApplySpaceToolRequest(
        this.mapId,
        mapPhase.id,
        payload,
      );

      if (errors) {
        throw errors;
      }

      return phaseStore
        .applySpaceTool(this.mapId, mapPhase.id, payload)
        .then(() => projectStore.loadProject(this.mapId));
    },

    async applySpaceFullProject(anchor: number, offset: number): Promise<void> {
      const projectStore = useProjectStore();
      const projectRulesStore = useProjectRulesStore();

      const startDate = projectStore.project(this.mapId).start_date;
      const deadlineDate = new Date(startDate);

      deadlineDate.setUTCDate(deadlineDate.getUTCDate() + anchor);

      const payload = {
        deadline: deadlineDate.toISOString(),
        offset: -offset,
      };

      const errors = projectRulesStore.validateApplySpaceToolRequest(
        this.mapId,
        payload,
      );

      if (errors) {
        throw errors;
      }

      await projectStore.applySpaceTool(this.mapId, payload);
      await projectStore.loadProject(this.mapId);
    },

    // the earliest position a intermediate milestone can be moved to when there
    // are activities linked to it (only currently-valid connections clamp)
    getIntermediateMilestoneIdealMinDate(msId: string) {
      const milestone = this.milestone(msId);
      const activities = this.validMilestoneActivities(msId);

      if (!activities.length) {
        if (this.isTemplate) {
          return this.phase(milestone.phase).start + 1;
        }

        return this.getIntermediateMilestonePermissibleMinDate(msId);
      }

      return Math.max(...activities.map((a) => a.originalPosition));
    },

    // Positions occupied by the other (non-mirrored) milestones. No two
    // milestones may share a deadline, so the intermediate-milestone drags
    // (canvas + SVG) skip these exact days.
    otherMilestonePositions(msId: string): Set<number> {
      return new Set(
        this.milestones
          .filter((m) => m.id !== msId && !m.mirrored)
          .map((m) => m.originalPosition),
      );
    },

    // the earliest position a intermediate milestone can be moved to — bounded
    // by the surrounding done/unplanned phases (an unplanned milestone may enter unplanned phases)
    getIntermediateMilestonePermissibleMinDate(msId: string) {
      const milestone = this.milestone(msId);
      const milestoneIsNotUnplanned = milestone.state !== 'unplanned';

      const lastPhase = Math.max(
        ...this.phases
          .filter(
            (phase) =>
              milestone.originalPosition > phase.end &&
              (phase.state === 'done' ||
                (phase.state === 'unplanned' && milestoneIsNotUnplanned)),
          )
          .map((phase) => phase.end),
      );

      return isFinite(lastPhase) ? lastPhase + 1 : 1;
    },

    // the latest position a intermediate milestone can be moved to when there
    // are activities linked to it (only currently-valid connections keep it in
    // its phase)
    getIntermediateMilestoneIdealMaxDate(msId: string) {
      const milestone = this.milestone(msId);
      if (!this.validMilestoneActivities(msId).length && !this.isTemplate) {
        return this.getIntermediateMilestonePermissibleMaxDate(msId);
      }

      return this.phase(milestone.phase).end - 1;
    },

    // the latest position a intermediate milestone can be moved to — bounded
    // by the surrounding done/unplanned phases (an unplanned
    // milestone may enter unplanned phases)
    getIntermediateMilestonePermissibleMaxDate(msId: string) {
      const milestone = this.milestone(msId);
      const milestoneIsNotUnplanned = milestone.state !== 'unplanned';

      const nextPhaseStart = Math.min(
        ...this.phases
          .filter(
            (phase) =>
              milestone.originalPosition < phase.start &&
              (phase.state === 'done' ||
                (phase.state === 'unplanned' && milestoneIsNotUnplanned)),
          )
          .map((phase) => phase.start),
      );

      return (isFinite(nextPhaseStart) ? nextPhaseStart : this.mapLength) - 1;
    },

    // the earliest position a final milestone can be moved to
    getFinalMilestoneMinDate(msId: string) {
      const milestone = this.milestone(msId);
      const activities = this.activitiesInPhase(milestone.phase);
      const phaseStart = this.phase(milestone.phase).start;

      return calculateFinalMilestoneMinDeadlinePosition(
        phaseStart,
        activities,
        milestone,
      );
    },

    // the latest position a final milestone can be moved to
    getFinalMilestoneMaxDate(msId: string) {
      const milestone = this.milestone(msId);
      const prePhase = this.phase(milestone.phase);
      const nextPhase = this.phases.find((p) => p.idx === prePhase.idx + 1);
      if (!nextPhase) {
        return;
      }

      const nextMilestone = this.milestones.find(
        (msItem) => msItem.idx === milestone.idx + 1,
      );
      const activitiesInPhase = this.activitiesInPhase(nextPhase.id);

      return calculateFinalMilestoneMaxDeadlinePosition(
        nextPhase,
        activitiesInPhase,
        nextMilestone,
      );
    },
    togglePhaseVisibility(phase: MapPhase) {
      const mapStore = useMapStore();

      if (this.hiddenPhases.has(phase.id)) {
        this.hiddenPhases.delete(phase.id);
      } else {
        this.hiddenPhases.set(phase.id, phase.idx);
      }

      const newHiddenPhases = new Map<string, number>();
      // there should be no hidden phases between first & last visible phases
      this.hiddenPhases.forEach((idx, id) => {
        if (
          idx < this.firstVisiblePhase.idx ||
          idx > this.lastVisiblePhase.idx
        ) {
          newHiddenPhases.set(id, idx);
        }
      });
      this.hiddenPhases = newHiddenPhases;

      mapStore.animateMap(mapStore.zoom, this.firstVisiblePhase.start);

      localStorage.setItem(
        `hiddenPhases-${this.mapId}`,
        JSON.stringify([...this.hiddenPhases]),
      );
    },

    // WI #5563: toggle the temporary expand (solo / focus) on a workstream.
    // Expanding clears any previous expand; clicking the expanded one collapses.
    // Never touches `hiddenWorkstreams` / localStorage.
    toggleWorkstreamExpand(stream: MapWorkstream) {
      this.expandedWorkstreamId =
        this.expandedWorkstreamId === stream.id ? null : stream.id;
    },

    // WI #5563: leave expand mode, restoring the persisted visible/hidden state
    // (which was never mutated). Idempotent.
    collapseWorkstream() {
      if (this.expandedWorkstreamId !== null) {
        this.expandedWorkstreamId = null;
      }
    },

    toggleWorkstreamVisibility(stream: MapWorkstream) {
      if (this.hiddenWorkstreams.has(stream.id)) {
        this.hiddenWorkstreams.delete(stream.id);
      } else {
        this.hiddenWorkstreams.set(stream.id, stream.position);
      }

      localStorage.setItem(
        `hiddenWorkstreams-${this.mapId}`,
        JSON.stringify([...this.hiddenWorkstreams]),
      );
    },

    showAllHidden() {
      localStorage.removeItem(`hiddenPhases-${this.mapId}`);
      localStorage.removeItem(`hiddenWorkstreams-${this.mapId}`);

      this.hiddenPhases.clear();
      this.hiddenWorkstreams.clear();
    },

    showAllHiddenWorkstreams() {
      localStorage.removeItem(`hiddenWorkstreams-${this.mapId}`);
      this.hiddenWorkstreams.clear();
    },

    loadHiddenPhases() {
      const hiddenPhases = localStorage.getItem(`hiddenPhases-${this.mapId}`);
      if (hiddenPhases) {
        const parsedHiddenPhases = JSON.parse(hiddenPhases);
        this.hiddenPhases = new Map<string, number>(parsedHiddenPhases);
      }
    },

    loadHiddenWorkstreams() {
      // WI #5563: the expand (solo) flag is temporary and map-scoped — switching
      // maps (the only caller, RemapPage) always drops it.
      this.expandedWorkstreamId = null;
      const hiddenWorkstreams = localStorage.getItem(
        `hiddenWorkstreams-${this.mapId}`,
      );
      if (hiddenWorkstreams) {
        const parsedHiddenWorkstreams = JSON.parse(hiddenWorkstreams);
        this.hiddenWorkstreams = new Map<string, number>(
          parsedHiddenWorkstreams,
        );
      }
    },
  },
});

function functionHolders(projectId: string, functionIds: string[]) {
  const projectStore = useProjectStore();

  return functionIds
    .filter((fnId) => projectStore.hasProjectFunction(projectId, fnId))
    .map((fnId) => projectStore.projectFunction(projectId, fnId).user)
    .filter((user): user is UserSummary => !!user);
}

function getResponsibleUser(projectId: string, responsible: Assignment) {
  if (responsible.special_assignment) {
    return responsible.user;
  }

  return responsible.function_id
    ? useProjectStore().projectFunction(projectId, responsible.function_id).user
    : null;
}
